import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, Cell, Tooltip } from 'recharts';

// Recharts forwards `fill` / `stroke` / `cursor.fill` props to native SVG
// attributes, which do NOT resolve CSS `var(--...)` custom properties, so we
// resolve tokens to concrete hex strings at runtime.
import { resolveToken } from './themeTokens';

const MOCK_AGE_BUCKETS = [
  { label: '18 – 24', count: 22,  pct: 8 },
  { label: '25 – 34', count: 113, pct: 41 },
  { label: '35 – 44', count: 91,  pct: 33 },
  { label: '45 – 54', count: 39,  pct: 14 },
  { label: '55 +',    count: 10,  pct: 4 },
];

const AGE_TONES = [
  'var(--setlist-blue-200)',
  'var(--setlist-blue-500)',
  'var(--setlist-blue-600)',
  'var(--setlist-blue-700)',
  'var(--setlist-indigo-700)',
];

const GENDER_COLOR_BY_KIND = {
  male:    'var(--setlist-blue-500)',
  female:  'var(--setlist-purple-500)',
  unknown: 'var(--setlist-gray-300)',
};

const MOCK_GENDER = [
  { kind: 'male',    label: 'Male',    short: 'M', count: 198, pct: 72 },
  { kind: 'female',  label: 'Female',  short: 'F', count: 72,  pct: 26 },
  { kind: 'unknown', label: 'No data', short: '?', count: 5,   pct: 2 },
];

const formatCount = (n) => new Intl.NumberFormat('en-US').format(n);

const Header = ({ uniqueBuyers, isSample }) => (
  <div className="mi-audience-card-head">
    <div>
      <h3 className="mi-audience-card-title">Demographics</h3>
      <p className="mi-audience-card-subtitle">
        {uniqueBuyers > 0
          ? `Age & gender of ${formatCount(uniqueBuyers)} buyer${uniqueBuyers === 1 ? '' : 's'}`
          : 'Age & gender of buyers'}
      </p>
    </div>
    {isSample && <span className="mi-sample-pill">Sample data</span>}
  </div>
);

const SectionHeading = ({ children }) => (
  <h4 className="mi-demographics-section-heading">{children}</h4>
);

const AgeBars = ({ buckets }) => (
  <ul className="mi-demographics-age-list">
    {buckets.map((row, i) => (
      <li key={row.label} className="mi-demographics-age-row">
        <span className="mi-demographics-age-label">{row.label}</span>
        <span className="mi-demographics-age-track">
          <span
            className="mi-demographics-age-fill"
            style={{ width: `${row.pct}%`, background: AGE_TONES[i] }}
          />
        </span>
        <span className="mi-demographics-age-value">{row.pct}%</span>
      </li>
    ))}
  </ul>
);

const GenderStacked = ({ rows }) => (
  <div className="mi-demographics-gender-stacked">
    {rows
      .filter((g) => g.pct > 0)
      .map((g) => (
        <span
          key={g.kind || g.label}
          className="mi-demographics-gender-segment"
          style={{ width: `${g.pct}%`, background: GENDER_COLOR_BY_KIND[g.kind] || 'var(--setlist-gray-300)' }}
        >
          {g.pct >= 10 && <span>{g.short} · {g.pct}%</span>}
        </span>
      ))}
  </div>
);

const GenderLegend = ({ rows }) => (
  <ul className="mi-demographics-gender-legend">
    {rows.map((g) => (
      <li key={g.kind || g.label}>
        <span
          className="mi-demographics-gender-dot"
          style={{ background: GENDER_COLOR_BY_KIND[g.kind] || 'var(--setlist-gray-300)' }}
        />
        {g.label} {formatCount(g.count)}
      </li>
    ))}
  </ul>
);

const detectSlowingStartIndex = (timing) => {
  if (timing.length < 5) return null;
  const peak = timing.reduce((m, t) => Math.max(m, t.orders || 0), 0);
  if (peak <= 0) return null;
  const tailLen = Math.min(3, Math.floor(timing.length / 3));
  const tailStart = timing.length - tailLen;
  const tailAvg = timing.slice(tailStart).reduce((s, t) => s + (t.orders || 0), 0) / tailLen;
  return tailAvg < peak * 0.4 ? tailStart : null;
};

const parseWeekNum = (week) => parseInt(String(week).replace(/[^0-9]/g, ''), 10);

const TimingTick = ({ x, y, payload, slowingStartIndex, indexByWeek, tokens }) => {
  const idx = indexByWeek.get(payload.value);
  if (idx == null) return null;
  const weekNum = parseWeekNum(payload.value);

  // Stack the week and "↓ slowing" annotation on two lines — the combined
  // single-line label is wide enough to collide with the adjacent week tick
  // when the bars are tightly packed.
  if (slowingStartIndex !== null && idx === slowingStartIndex) {
    return (
      <text x={x} y={y + 14} fontSize={11} fontWeight={600} fill={tokens.orange700} textAnchor="middle">
        <tspan x={x}>Week {weekNum}</tspan>
        <tspan x={x} dy={13}>↓ slowing</tspan>
      </text>
    );
  }

  let label = null;
  if (weekNum === 1) {
    label = 'On-sale';
  } else if (weekNum > 1 && weekNum % 5 === 0) {
    label = `Week ${weekNum}`;
  }

  if (!label) return null;
  return (
    <text x={x} y={y + 14} fontSize={11} fill={tokens.textTertiary} textAnchor="middle">
      {label}
    </text>
  );
};

const PurchaseTimingChart = ({ timing }) => {
  const slowingStartIndex = detectSlowingStartIndex(timing);
  const indexByWeek = React.useMemo(
    () => new Map(timing.map((t, i) => [t.week, i])),
    [timing]
  );
  const tokens = React.useMemo(() => ({
    orange500: resolveToken('--setlist-orange-500', '#EF6820'),
    orange700: resolveToken('--setlist-orange-700', '#B93815'),
    blue500: resolveToken('--setlist-blue-500', '#2E90FA'),
    textTertiary: resolveToken('--sl-text-tertiary', '#535862'),
    bgSecondary: resolveToken('--sl-bg-secondary', '#FAFAFA'),
    bgPrimary: resolveToken('--sl-bg-primary', '#FFFFFF'),
    textPrimary: resolveToken('--sl-text-primary', '#181D27'),
    borderSecondary: resolveToken('--sl-border-secondary', '#E9EAEB'),
  }), []);
  return (
    <div className="mi-demographics-timing-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={timing} margin={{ top: 4, right: 4, bottom: 30, left: 4 }}>
          <XAxis
            dataKey="week"
            tickLine={false}
            axisLine={false}
            interval={0}
            tick={
              <TimingTick
                slowingStartIndex={slowingStartIndex}
                indexByWeek={indexByWeek}
                tokens={tokens}
              />
            }
          />
          <Tooltip
            cursor={{ fill: tokens.bgSecondary }}
            contentStyle={{
              fontSize: 12,
              padding: '6px 10px',
              borderRadius: 6,
              background: tokens.bgPrimary,
              border: `1px solid ${tokens.borderSecondary}`,
            }}
            labelStyle={{ color: tokens.textPrimary }}
            itemStyle={{ color: tokens.textPrimary }}
          />
          <Bar dataKey="orders" radius={[3, 3, 0, 0]}>
            {timing.map((_, i) => (
              <Cell
                key={i}
                fill={
                  slowingStartIndex !== null && i >= slowingStartIndex
                    ? tokens.orange500
                    : tokens.blue500
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const DemographicsCard = ({ purchaseTiming, uniqueBuyers = 0, demographics }) => {
  const timing = Array.isArray(purchaseTiming) ? purchaseTiming : [];
  const hasTiming = timing.length > 0;

  const ageIsReal = Array.isArray(demographics?.age_buckets);
  const genderIsReal = Array.isArray(demographics?.gender_breakdown);
  const isSample = !ageIsReal && !genderIsReal;
  const ageBuckets = ageIsReal ? demographics.age_buckets : MOCK_AGE_BUCKETS;
  const genderRows = genderIsReal ? demographics.gender_breakdown : MOCK_GENDER;
  const ageHasData = ageBuckets.some((b) => b.count > 0);
  const genderHasData = genderRows.some((g) => g.count > 0);

  return (
    <section className="mi-audience-card mi-demographics-card" aria-label="Demographics">
      <Header uniqueBuyers={uniqueBuyers} isSample={isSample} />

      <div className="mi-demographics-section">
        <SectionHeading>By age</SectionHeading>
        {ageIsReal && !ageHasData ? (
          <p className="mi-audience-empty">Age data unavailable yet.</p>
        ) : (
          <AgeBars buckets={ageBuckets} />
        )}
      </div>

      <div className="mi-demographics-section">
        <SectionHeading>By gender</SectionHeading>
        {genderIsReal && !genderHasData ? (
          <p className="mi-audience-empty">Gender data unavailable yet.</p>
        ) : (
          <>
            <GenderStacked rows={genderRows} />
            <GenderLegend rows={genderRows} />
          </>
        )}
      </div>

      <div className="mi-demographics-section">
        <SectionHeading>Purchase timing</SectionHeading>
        {hasTiming ? (
          <PurchaseTimingChart timing={timing} />
        ) : (
          <p className="mi-audience-empty">No paid orders since on-sale yet.</p>
        )}
      </div>
    </section>
  );
};

export default DemographicsCard;
